// Placement diagnostic: same question formats as grammar engine (MC, fill-in, sentence building, error correction), compact text, no auto-advance.
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Type, Layers, Headphones, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getCorrectIndex } from './placementQuestions';

const SPRING = { type: 'spring', stiffness: 260, damping: 30 };

/** Normalize for sentence-building comparison: ignore punctuation (comma, period) and collapse spaces. */
function normalizeSentenceForCompare(s) {
  return (s || '')
    .replace(/[,.]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// Compact option card (smaller text for long sentences)
function OptionCardCompact({ selected, onClick, label, disabled, isCorrect, isWrong }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      whileTap={disabled ? {} : { scale: 0.99 }}
      className={`
        w-full text-left rounded-xl border-2 min-h-[44px] px-3 py-2.5
        text-sm leading-snug transition-colors touch-manipulation
        ${selected && !disabled ? 'bg-black border-black text-white' : ''}
        ${disabled && isCorrect ? 'bg-emerald-100 border-emerald-400 text-emerald-900' : ''}
        ${disabled && isWrong ? 'bg-red-50 border-red-300 text-red-900' : ''}
        ${!selected && !disabled && !isCorrect && !isWrong ? 'bg-white border-black/20 hover:border-black/50 text-black' : ''}
      `}
    >
      {label}
    </motion.button>
  );
}

export default function PlacementDiagnosticSlide({
  question,
  questionIndex,
  totalQuestions,
  placementAnswer,
  onSelect,
  typeLabel,
  TypeIcon
}) {
  const { t } = useTranslation();
  const hasAnswered = placementAnswer != null;
  const correctIndex = getCorrectIndex(question);

  //, , State per exercise type, , 
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [fillInAnswers, setFillInAnswers] = useState({});
  const [wordBank, setWordBank] = useState([]);
  const [sentenceArea, setSentenceArea] = useState([]);
  const [correctedText, setCorrectedText] = useState('');

  const exerciseType = question.exerciseType || 'multipleChoice';

  // Initialize sentence building word bank
  useEffect(() => {
    if (exerciseType === 'sentenceBuilding' && question.stimulus && Array.isArray(question.stimulus)) {
      const shuffled = [...question.stimulus].sort(() => Math.random() - 0.5);
      setWordBank(shuffled.map((w, i) => ({ id: `w-${i}-${Date.now()}`, text: w })));
      setSentenceArea([]);
    }
  }, [question.id, exerciseType, question.stimulus]);

  const isCorrect = () => {
    if (exerciseType === 'multipleChoice') return placementAnswer === correctIndex;
    return placementAnswer === true;
  };

  const handleMultipleChoiceSelect = (idx) => {
    if (hasAnswered) return;
    const optionText = question.options[idx];
    setSelectedAnswer(optionText);
    const correct = idx === correctIndex;
    onSelect(questionIndex, idx);
  };

  const handleFillInSubmit = () => {
    if (hasAnswered) return;
    const answers = question.answers || [];
    const allCorrect = answers.every((a, i) =>
      (fillInAnswers[`blank_${i}`] || '').trim().toLowerCase() === (a.correct || '').trim().toLowerCase()
    );
    onSelect(questionIndex, allCorrect);
  };

  const handleSentenceBuildingSubmit = () => {
    if (hasAnswered) return;
    const built = sentenceArea.map((w) => w.text).join(' ').trim();
    const correct = normalizeSentenceForCompare(built) === normalizeSentenceForCompare(question.correctAnswer || '');
    onSelect(questionIndex, correct);
  };

  const handleErrorCorrectionSubmit = () => {
    if (hasAnswered) return;
    const full = (question.stimulus || '').replace(question.incorrectPart, correctedText || question.incorrectPart);
    const correct = full.trim().toLowerCase() === (question.correctStimulus || '').trim().toLowerCase();
    onSelect(questionIndex, correct);
  };

  const addWordToSentence = (word) => {
    if (hasAnswered) return;
    const newWord = { ...word, id: `s-${Date.now()}-${Math.random()}` };
    setSentenceArea((prev) => [...prev, newWord]);
    setWordBank((prev) => prev.filter((w) => w.id !== word.id));
  };

  const removeWordFromSentence = (word) => {
    if (hasAnswered) return;
    setWordBank((prev) => [...prev, { ...word, id: `b-${Date.now()}-${Math.random()}` }]);
    setSentenceArea((prev) => prev.filter((w) => w.id !== word.id));
  };

  const shuffleBank = () => {
    if (hasAnswered) return;
    setWordBank((prev) => [...prev].sort(() => Math.random() - 0.5));
  };

  const clearSentence = () => {
    if (hasAnswered) return;
    setWordBank((prev) => [...prev, ...sentenceArea.map((w) => ({ ...w, id: `b-${Date.now()}-${Math.random()}` }))]);
    setSentenceArea([]);
  };

  //, , Render by type (compact: text-sm), , 
  const renderContent = () => {
    if (exerciseType === 'multipleChoice') {
      const options = question.options || [];
      return (
        <div className="space-y-2">
          {question.stimulus && (
            <p className="text-sm text-black/80 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              {question.stimulus}
            </p>
          )}
          {options.map((opt, idx) => (
            <OptionCardCompact
              key={idx}
              selected={placementAnswer === idx}
              onClick={() => handleMultipleChoiceSelect(idx)}
              disabled={hasAnswered}
              isCorrect={hasAnswered && idx === correctIndex}
              isWrong={hasAnswered && placementAnswer === idx && placementAnswer !== correctIndex}
              label={opt}
            />
          ))}
        </div>
      );
    }

    if (exerciseType === 'fillInTheBlanks') {
      const stimulus = question.stimulus || '';
      const parts = stimulus.split(/(______)|(__+)|(\[---\])/g).filter(Boolean);
      const blanks = question.answers || [];
      let blankIdx = 0;
      return (
        <div className="space-y-3">
          <p className="text-sm text-black/80 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            {parts.map((part, i) => {
              if (part.match(/^__+$/) || part === '[---]' || part === '______') {
                const idx = blankIdx++;
                const correctVal = blanks[idx]?.correct || '';
                const userVal = fillInAnswers[`blank_${idx}`] || '';
                const isBlankCorrect = hasAnswered && userVal.trim().toLowerCase() === correctVal.trim().toLowerCase();
                return (
                  <span key={i}>
                    {' '}
                    <input
                      type="text"
                      className={`inline-block mx-1 px-2 py-1 border-2 rounded text-sm min-w-[80px] max-w-[140px] text-center touch-manipulation ${
                        hasAnswered ? (isBlankCorrect ? 'border-emerald-500 bg-emerald-50' : 'border-red-400 bg-red-50') : 'border-black/30'
                      }`}
                      style={{ fontSize: '14px' }}
                      disabled={hasAnswered}
                      value={userVal}
                      onChange={(e) => setFillInAnswers((prev) => ({ ...prev, [`blank_${idx}`]: e.target.value }))}
                      placeholder="..."
                    />
                    {' '}
                  </span>
                );
              }
              return <span key={i}>{part}</span>;
            })}
          </p>
          {!hasAnswered && (
            <motion.button
              type="button"
              onClick={handleFillInSubmit}
              whileTap={{ scale: 0.98 }}
              className="mt-2 px-4 py-2 rounded-xl bg-black text-white text-sm font-bold touch-manipulation"
            >
              {t('onboarding.diagnostic.check', 'Check')}
            </motion.button>
          )}
        </div>
      );
    }

    if (exerciseType === 'sentenceBuilding') {
      return (
        <div className="space-y-3">
          <p className="text-xs text-black/60 mb-1">{t('grammar.exercise.sentenceBuilder.tapInstruction', 'Tap words to build the sentence.')}</p>
          <div className="flex flex-wrap gap-1.5 p-2 bg-black/[0.04] rounded-xl min-h-[52px]">
            {wordBank.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => addWordToSentence(w)}
                disabled={hasAnswered}
                className="px-2.5 py-1.5 rounded-lg border-2 border-black/20 text-sm font-medium touch-manipulation hover:bg-black/5 disabled:opacity-50"
              >
                {w.text}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-black/60">Ihr Satz:</span>
            {sentenceArea.length > 0 && !hasAnswered && (
              <button type="button" onClick={clearSentence} className="flex items-center gap-1 text-xs text-red-600">
                <Trash2 className="w-3.5 h-3.5" /> Löschen
              </button>
            )}
          </div>
          <div className="min-h-[52px] p-2 bg-white border-2 border-black/20 rounded-xl flex flex-wrap gap-1.5">
            {sentenceArea.length === 0 && !hasAnswered && (
              <span className="text-sm text-black/40 italic">Tippen Sie auf Wörter oben</span>
            )}
            {sentenceArea.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => removeWordFromSentence(w)}
                disabled={hasAnswered}
                className="px-2.5 py-1.5 rounded-lg bg-black text-white text-sm font-medium touch-manipulation disabled:opacity-70"
              >
                {w.text}
              </button>
            ))}
          </div>
          {!hasAnswered && (
            <motion.button
              type="button"
              onClick={handleSentenceBuildingSubmit}
              disabled={sentenceArea.length === 0}
              whileTap={{ scale: 0.98 }}
              className="mt-2 px-4 py-2 rounded-xl bg-black text-white text-sm font-bold touch-manipulation disabled:opacity-40"
            >
              {t('onboarding.diagnostic.check', 'Check')}
            </motion.button>
          )}
          {hasAnswered && question.correctAnswer && (
            <p className="text-sm mt-2 p-2 rounded-lg bg-black/5 border border-black/10">
              <span className="font-medium text-black/70">Musterlösung:</span> {question.correctAnswer}
            </p>
          )}
        </div>
      );
    }

    if (exerciseType === 'errorCorrection') {
      const before = (question.stimulus || '').substring(0, (question.stimulus || '').indexOf(question.incorrectPart || ''));
      const after = (question.stimulus || '').substring((question.stimulus || '').indexOf(question.incorrectPart || '') + (question.incorrectPart || '').length);
      return (
        <div className="space-y-3">
          <p className="text-sm text-black/80 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            <span>{before}</span>
            <input
              type="text"
              className={`inline-block mx-1 px-2 py-1 border-2 rounded text-sm min-w-[80px] max-w-[160px] touch-manipulation ${
                hasAnswered ? (isCorrect() ? 'border-emerald-500 bg-emerald-50' : 'border-red-400 bg-red-50') : 'border-amber-300 bg-amber-50'
              }`}
              style={{ fontSize: '14px' }}
              disabled={hasAnswered}
              value={correctedText || question.incorrectPart || ''}
              onChange={(e) => setCorrectedText(e.target.value)}
              placeholder="Korrigieren..."
            />
            <span>{after}</span>
          </p>
          {!hasAnswered && (
            <motion.button
              type="button"
              onClick={handleErrorCorrectionSubmit}
              whileTap={{ scale: 0.98 }}
              className="mt-2 px-4 py-2 rounded-xl bg-black text-white text-sm font-bold touch-manipulation"
            >
              {t('onboarding.diagnostic.check', 'Check')}
            </motion.button>
          )}
          {hasAnswered && question.correctStimulus && (
            <p className="text-sm mt-2 p-2 rounded-lg bg-black/5 border border-black/10">
              <span className="font-medium text-black/70">Korrekte Lösung:</span> {question.correctStimulus}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="mb-3">
        <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-black/[0.04] px-2.5 py-1 text-xs font-medium text-black/70 uppercase tracking-wider">
          {TypeIcon && <TypeIcon className="w-3.5 h-3.5 text-black/50" aria-hidden />}
          {typeLabel}
        </span>
      </div>
      <h2 className="font-space text-base sm:text-lg text-black leading-snug mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
        {question.question}
      </h2>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {renderContent()}
      </div>
    </div>
  );
}
